import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { parseTimeInput } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import WheelTimePicker from "./WheelTimePicker";

interface SmartTimeInputProps {
  value: string;
  onChange: (time: string) => void;
  onDuration?: (parsed: { hours: number; minutes: number }) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}

/**
 * Smart time input that accepts flexible formats:
 * "8" → 08:00, "1430" → 14:30, "9.5" → 09:30, "2t" → duration
 *
 * Parsing happens on BLUR, not on every keystroke, to avoid
 * mangling intermediate states like "0" or "1".
 * A preview hint shows for unambiguous inputs while typing.
 */
export default function SmartTimeInput({
  value,
  onChange,
  onDuration,
  placeholder = 'f.eks. "8", "1430"',
  autoFocus,
  className,
}: SmartTimeInputProps) {
  const [raw, setRaw] = useState(value || "");
  const [hint, setHint] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const isExternalUpdate = useRef(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      if (value) inputRef.current.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only refocus/select on autoFocus toggle, not on every value change
  }, [autoFocus]);

  // Sync external value changes (gap-filling, quick buttons, post-save reset)
  // Select all text so next keystroke replaces the auto-filled value
  useEffect(() => {
    if (value !== undefined) {
      isExternalUpdate.current = true;
      setRaw(value);
      setHint("");
      if (value && inputRef.current && document.activeElement === inputRef.current) {
        requestAnimationFrame(() => {
          // The user may have tabbed on before this frame runs; selecting a
          // field that is no longer active would steal focus back (VIB-2526).
          if (document.activeElement === inputRef.current) inputRef.current?.select();
        });
      }
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    isExternalUpdate.current = false;
    setRaw(v);

    // Only show hint for unambiguous inputs (don't resolve short digit strings mid-typing)
    const parsed = parseTimeInput(v);
    if (!parsed) {
      setHint("");
      return;
    }

    if (parsed.type === "duration") {
      // Duration hints are always safe to show (requires "t" or "m" suffix)
      const durLabel = `+${parsed.hours > 0 ? parsed.hours + "t" : ""}${parsed.minutes > 0 ? parsed.minutes + "m" : ""}`;
      setHint(durLabel);
    } else if (parsed.type === "time") {
      // Only show hint for inputs that are "complete enough":
      // - 3+ digits (e.g. "830" → 08:30)
      // - contains colon (e.g. "8:00")
      // - contains dot (e.g. "9.5")
      const isUnambiguous = v.length >= 3 || v.includes(":") || v.includes(".") || v.includes(",");
      setHint(isUnambiguous && v !== parsed.value ? parsed.value : "");
    }
  };

  const resolve = () => {
    const trimmed = raw.trim();
    if (!trimmed) {
      onChange("");
      setHint("");
      return;
    }

    const parsed = parseTimeInput(trimmed);
    if (!parsed) return;

    if (parsed.type === "time") {
      setRaw(parsed.value);
      setHint("");
      onChange(parsed.value);
    } else if (parsed.type === "duration") {
      // Commit duration via callback, parent resolves to actual time
      onDuration?.(parsed);
      setHint("");
    }
  };

  const handleBlur = () => {
    if (!isExternalUpdate.current) {
      resolve();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Tab") {
      // Resolve before tabbing to next field
      resolve();
    }
    // Enter is handled by parent (TimeTrackingPopup onKeyDown)
  };

  // Mobile: iOS-style wheel picker (consistent across platforms)
  if (isMobile) {
    const wheelValue = /^\d{2}:\d{2}$/.test(value) ? value : "";
    return (
      <WheelTimePicker
        value={wheelValue}
        onChange={onChange}
        placeholder="Velg tid"
        className={className}
      />
    );
  }

  // Desktop: smart text input with "8", "1430", "2t" parsing
  return (
    <div className={cn("relative", className)}>
      <Input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={raw}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="h-8 text-xs font-mono pr-14"
      />
      {hint && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground pointer-events-none">
          → {hint}
        </span>
      )}
    </div>
  );
}
